import { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    q: 'Who is the best real estate agent in Lake Tahoe, CA?',
    a: 'The best real estate agent in Lake Tahoe combines deep local knowledge, alpine resort experience, and a proven record of high-value transactions. Cam Kirkwood offers all three — specializing in luxury home sales, buyer representation, vacation properties, and investment real estate across Lake Tahoe and the Sierra Nevada since 2004.',
  },
  {
    q: 'How much do homes cost in Lake Tahoe?',
    a: 'Lake Tahoe and Sierra Nevada home prices vary significantly based on location, size, and amenities. Ski-access properties and lakefront estates typically range from $800K to over $5M. South Lake Tahoe homes range widely by proximity to the lake. Most second homes in the region sell between $600K and $2.5M depending on location and seasonal access.',
  },
  {
    q: 'Is Lake Tahoe a good place to buy a second home?',
    a: "Yes — Lake Tahoe is an excellent second-home market. World-class ski access, pristine alpine scenery, and limited property inventory create strong long-term value. Seasonal vacation rental demand adds income potential for owners who don't occupy year-round. Lake Tahoe properties consistently appreciate due to constrained supply and growing buyer interest.",
  },
  {
    q: 'How long does it take to sell a home in Lake Tahoe?',
    a: "Market timing in Lake Tahoe is seasonal. Peak buyer activity occurs during ski season (December\u2013March) and summer (June\u2013August). With expert pricing strategy and premium marketing, well-positioned properties typically sell within 30\u201390 days. Cam Kirkwood\u2019s strategic approach consistently minimizes days on market and maximizes the final sale price.",
  },
  {
    q: 'What should I know before buying real estate in Lake Tahoe?',
    a: 'Before buying in Lake Tahoe, understand HOA regulations in resort communities, short-term rental rules (which vary by jurisdiction), altitude and snow impacts on maintenance costs, and seasonal access limitations. Working with a Lake Tahoe and Sierra Nevada specialist like Cam Kirkwood ensures you navigate every complexity with confidence and clarity.',
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }
      },
      { threshold: 0.05 }
    );
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    el.style.transition = 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)';
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 py-28 px-4 md:px-8 bg-warm-white rounded-t-[2.5rem] -mt-8">
      {/* Subtle background dots */}
      <div
        className="absolute inset-0 opacity-[0.01] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #2A2A2A 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">Common Questions</p>
          <h2 className="font-serif text-5xl md:text-7xl text-charcoal font-light">
            Frequently <em className="italic">Asked</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/60" />
            <div className="w-16 h-px bg-gold/40" />
          </div>
          <p className="text-stone text-sm max-w-lg mx-auto mt-6 leading-relaxed">
            Answers optimized for both buyers and voice search — covering cost, timeline, and everything in between for the Lake Tahoe and Sierra Nevada real estate market.
          </p>
        </div>

        {/* FAQ items */}
        <div ref={sectionRef} className="space-y-3">
          {faqs.map((item, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
              style={{
                border: open === i ? '1px solid rgba(196,169,98,0.35)' : '1px solid rgba(240,235,224,1)',
                background: open === i ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.6)',
                boxShadow: open === i ? '0 6px 28px rgba(42,42,42,0.08)' : 'none',
              }}
              onClick={() => setOpen(open === i ? null : i)}
            >
              <button className="w-full flex items-start justify-between px-7 py-6 text-left gap-4">
                <span className="font-serif text-lg md:text-xl text-charcoal font-medium leading-snug">{item.q}</span>
                <span
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full mt-0.5 transition-all duration-300 ${
                    open === i
                      ? 'bg-gold border-gold text-white rotate-45'
                      : 'border border-stone/25 text-stone/50'
                  }`}
                >
                  <i className="ri-add-line text-sm" />
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-500 ${open === i ? 'max-h-72' : 'max-h-0'}`}>
                <p className="px-7 pb-6 text-stone text-sm leading-relaxed">{item.a}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-stone text-sm mb-5">Still have questions? Let's talk.</p>
          <a
            href="#consultation"
            className="inline-block whitespace-nowrap px-8 py-4 bg-charcoal text-white text-[11px] tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal/80 transition-all duration-300"
          >
            Schedule a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
